import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { ConfirmDialog, Dialog, Field, SectionCard, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { TCard, TEmpty, TFoot, Toolbar, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './insights.css';

/*
 * Insights — the event's sessions ledger (search, select + merge,
 * thumbnails), the share journey (who opened what), and shares / media
 * by day. Rows open the session page.
 */

const PAGE = 30;
const JOURNEY_PAGE = 20;

const EVENT_LABELS: Record<string, string> = {
    'page-view': 'Opened gallery',
    'media-view': 'Viewed photo',
    download: 'Downloaded',
    'download-all': 'Downloaded all',
    reshare: 'Reshared',
    'cta-click': 'Clicked CTA',
};

const when = (s: any) => s.createdAt ?? s.date;
const fmtWhen = (v: any) => (v ? moment(v).format('MMM D · h:mm A') : '—');
const num = (v: any) => Number(v) || 0;

/** /activations/:id/sessions returns one row per (session, share): collapse to one per session. */
function collapseSessions(raw: any[]) {
    const byId = new Map<string, any>();
    raw.forEach((s) => {
        /* rows without a photoSessionId each stay separate (keyed by row id) */
        const k = s.photoSessionId ?? `row-${s.id}`;
        const prev = byId.get(k);
        if (!prev) byId.set(k, { ...s, _key: k });
        else {
            prev.sent = prev.sent || s.sent;
            prev.destination = prev.destination || s.destination;
        }
    });
    return Array.from(byId.values()).sort((a, b) => moment(when(b)).valueOf() - moment(when(a)).valueOf());
}

function Bar({ value, max }: { value: number; max: number }) {
    return (
        <span className="progress ins-bar" aria-hidden="true">
            <span className="fill" style={{ display: 'block', width: `${Math.max(4, (value / max) * 100)}%` }} />
        </span>
    );
}

/* ---- share journey ---- */

function ShareJourney({ id }: { id: number }) {
    const { getJourneySummary, getJourneyDestinations, getJourneyEventsForDestination } = useShare();
    const [summary, setSummary] = useState<any | null>(null);
    const [destinations, setDestinations] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
    const [attempt, setAttempt] = useState(0);
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreError, setMoreError] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [events, setEvents] = useState<any[] | null>(null);
    const [eventsFailed, setEventsFailed] = useState(false);
    const expandedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let mounted = true;
        setState('loading');
        Promise.all([getJourneySummary(id), getJourneyDestinations(id, JOURNEY_PAGE, 0)])
            .then(([s, d]) => {
                if (!mounted) return;
                setSummary(s ?? null);
                setDestinations(d?.destinations ?? []);
                setTotal(d?.total ?? 0);
                setState('ready');
            })
            .catch(() => {
                if (mounted) setState('failed');
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, attempt]);

    const loadMore = async () => {
        if (moreBusy) return;
        setMoreBusy(true);
        setMoreError(false);
        try {
            const page = await getJourneyDestinations(id, JOURNEY_PAGE, destinations.length);
            setDestinations((rows) => [...rows, ...(page?.destinations ?? [])]);
            setTotal(page?.total ?? 0);
        } catch {
            setMoreError(true);
        }
        setMoreBusy(false);
    };

    const toggle = async (slug: string | null) => {
        if (!slug || expanded === slug) {
            expandedRef.current = null;
            setExpanded(null);
            return;
        }
        expandedRef.current = slug;
        setExpanded(slug);
        setEvents(null);
        setEventsFailed(false);
        try {
            const rows = await getJourneyEventsForDestination(id, slug);
            /* ignore a slow answer for a row that's no longer open */
            if (expandedRef.current === slug) setEvents(Array.isArray(rows) ? rows : []);
        } catch {
            if (expandedRef.current === slug) {
                setEvents([]);
                setEventsFailed(true);
            }
        }
    };

    if (state === 'loading') {
        return (
            <SectionCard title="Share journey">
                <TSkeleton rows={3} />
            </SectionCard>
        );
    }
    if (state === 'failed') {
        return (
            <SectionCard title="Share journey">
                <TEmpty message="Couldn’t load the share journey." actionLabel="Retry" onAction={() => setAttempt((a) => a + 1)} />
            </SectionCard>
        );
    }
    if (!summary || !num(summary.uniqueDestinations)) {
        return (
            <SectionCard title="Share journey">
                <p className="quiet-empty">No shares yet — guest opens, views and downloads appear here once links go out.</p>
            </SectionCard>
        );
    }

    const openRate = Math.round(num(summary.openRate) * 100);
    const kpis: [string, string][] = [
        ['Reached', num(summary.uniqueDestinations).toLocaleString()],
        ['Opened', `${num(summary.openedDestinations).toLocaleString()} (${openRate}%)`],
        ['Visits', num(summary.totalVisits).toLocaleString()],
        ['Photos viewed', num(summary.mediaViews).toLocaleString()],
        ['Downloads', num(summary.downloads).toLocaleString()],
        ['Reshares', num(summary.reshares).toLocaleString()],
        ['CTA clicks', num(summary.ctaClicks).toLocaleString()],
    ];

    return (
        <SectionCard title="Share journey" note="link opens and what guests did in their gallery">
            <div className="ev-tiles">
                {kpis.map(([label, value]) => (
                    <div className="tile" key={label}>
                        <p className="tl">{label}</p>
                        <div className="tn">{value}</div>
                    </div>
                ))}
            </div>
            <table className="ins-table">
                <thead>
                    <tr>
                        <th scope="col">Destination</th>
                        <th scope="col">Visits</th>
                        <th scope="col" className="wide">
                            Photos viewed
                        </th>
                        <th scope="col" className="wide">
                            Downloads
                        </th>
                        <th scope="col" className="wide">
                            Reshares
                        </th>
                        <th scope="col">Last seen</th>
                    </tr>
                </thead>
                <tbody>
                    {destinations.map((d: any, i: number) => {
                        const slug: string | null = d.personalGallerySlug ?? null;
                        const open = slug !== null && expanded === slug;
                        return (
                            <React.Fragment key={slug ?? `${d.destination}-${i}`}>
                                <tr>
                                    <td className="dest">
                                        <button
                                            type="button"
                                            className="expand"
                                            disabled={!slug}
                                            aria-expanded={slug ? open : undefined}
                                            title={slug ? 'Show timeline' : undefined}
                                            onClick={() => toggle(slug)}
                                        >
                                            {d.destination || '—'}
                                        </button>
                                        <span className="badge">{d.type === 1 ? 'SMS' : 'Email'}</span>
                                    </td>
                                    <td className={num(d.visits) > 0 ? 'strong' : undefined}>{num(d.visits)}</td>
                                    <td className="wide">{num(d.mediaViews)}</td>
                                    <td className="wide">{num(d.downloads)}</td>
                                    <td className="wide">{num(d.reshares)}</td>
                                    <td>{d.lastSeenAt ? moment(d.lastSeenAt).format('MMM D · h:mm A') : '—'}</td>
                                </tr>
                                {open && (
                                    <tr>
                                        <td colSpan={6} className="timeline">
                                            {events === null ? (
                                                <span className="quiet-empty">Loading timeline…</span>
                                            ) : eventsFailed ? (
                                                <span className="field-e">Couldn’t load the timeline.</span>
                                            ) : events.length === 0 ? (
                                                <span className="quiet-empty">No activity yet.</span>
                                            ) : (
                                                events.map((e: any, ei: number) => (
                                                    <div className="ins-ev" key={e.id ?? ei}>
                                                        <span className="t">{fmtWhen(e.createdAt)}</span>
                                                        <span>{EVENT_LABELS[e.eventType] ?? e.eventType}</span>
                                                    </div>
                                                ))
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            {(total > destinations.length || moreError) && (
                <div className="ev-foot ev-gap">
                    <span className="quiet-empty">
                        {destinations.length.toLocaleString()} of {total.toLocaleString()}
                        {moreError && <span className="field-e"> · Couldn’t load more.</span>}
                    </span>
                    <button type="button" className="tb-btn" disabled={moreBusy} onClick={loadMore}>
                        {moreBusy ? 'Loading…' : 'Show more'}
                    </button>
                </div>
            )}
        </SectionCard>
    );
}

/* ---- client report link ---- */

function ReportLinkDialog({ id, open, onClose }: { id: number; open: boolean; onClose: () => void }) {
    const authenticatedApi = useAuthenticatedApi();
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!open || url) return;
        let mounted = true;
        setFailed(false);
        authenticatedApi
            .get(`/activations/${id}/reportLink`)
            .then((r) => r.data?.token as string | undefined)
            .catch(() => undefined)
            .then((token) => {
                if (!mounted) return;
                if (token) setUrl(`${window.location.origin}/report/${token}`);
                else setFailed(true);
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, id, attempt]);

    useEffect(() => {
        if (!copied) return;
        const t = window.setTimeout(() => setCopied(false), 1800);
        return () => window.clearTimeout(t);
    }, [copied]);

    const copy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            /* clipboard blocked: the field is selectable */
            document.getElementById('ins-report-url')?.focus();
        }
    };

    return (
        <Dialog
            open={open}
            title="Client report link"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose}>
                        Close
                    </button>
                    {failed ? (
                        <button type="button" className="tb-new" onClick={() => setAttempt((a) => a + 1)}>
                            Retry
                        </button>
                    ) : (
                        <button type="button" className="tb-new" disabled={!url} onClick={copy}>
                            {copied ? 'Copied' : 'Copy link'}
                        </button>
                    )}
                </>
            }
        >
            <p style={{ margin: '0 0 12px' }}>
                <span className="badge" style={{ marginLeft: 0, marginRight: 6 }}>
                    Public
                </span>
                Anyone with this link can see the event’s report — no sign-in needed. It shows event totals only. Share it only
                with your client.
            </p>
            <Field label="Report link" error={failed ? 'Couldn’t create the link — try again.' : undefined}>
                <TextInput
                    id="ins-report-url"
                    className="input ins-url"
                    readOnly
                    value={url ?? (failed ? '' : 'Creating link…')}
                    onFocus={(e) => e.currentTarget.select()}
                />
            </Field>
        </Dialog>
    );
}

/* ---- page ---- */

function InsightsBody() {
    const navigate = useNavigate();
    const { activationId: id, ensureMedia, media } = useEventContext();
    const { getActivationSessions } = useActivation();
    const { getShareStatsForActivation, mergeSessions } = useShare();
    const authenticatedApi = useAuthenticatedApi();
    const toast = useToast();

    const [sessions, setSessions] = useState<any[] | null>(null);
    const [sessionsFailed, setSessionsFailed] = useState(false);
    const [shareStats, setShareStats] = useState<any[] | null>(null);
    const [mediaStats, setMediaStats] = useState<any[] | null>(null);
    const [shareStatsFailed, setShareStatsFailed] = useState(false);
    const [mediaStatsFailed, setMediaStatsFailed] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [limit, setLimit] = useState(PAGE);
    const [confirmMerge, setConfirmMerge] = useState(false);
    const [merging, setMerging] = useState(false);
    const [mergeError, setMergeError] = useState<string | null>(null);
    const [reportOpen, setReportOpen] = useState(false);

    const loadSessions = useCallback(async () => {
        setSessionsFailed(false);
        try {
            const raw = await getActivationSessions(id);
            setSessions(collapseSessions(Array.isArray(raw) ? raw : []));
        } catch {
            setSessionsFailed(true);
        }
        setSelected([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadStats = useCallback(() => {
        setShareStatsFailed(false);
        setMediaStatsFailed(false);
        getShareStatsForActivation(id)
            .then((rows: any) => setShareStats(Array.isArray(rows) ? rows : []))
            .catch(() => setShareStatsFailed(true));
        authenticatedApi
            .get(`/activations/${id}/mediaStats`)
            .then((r) => setMediaStats(Array.isArray(r.data) ? r.data : []))
            .catch(() => setMediaStatsFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) return;
        loadSessions();
        loadStats();
        ensureMedia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* up to three thumbnails per session, from the shared media cache */
    const thumbs = useMemo(() => {
        const map: Record<string, string[]> = {};
        (media ?? []).forEach((m: any) => {
            const k = m.photoSessionId;
            const uri = m.thumbnailTinyUri ?? m.thumbnailUri;
            if (!k || !uri) return;
            const list = (map[k] = map[k] ?? []);
            if (list.length < 3) list.push(uri);
        });
        return map;
    }, [media]);

    const all = useMemo(() => sessions ?? [], [sessions]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return all.filter((s) => !q || `${s.destination ?? ''}`.toLowerCase().includes(q));
    }, [all, search]);
    const sharedCount = all.filter((s) => s.destination).length;

    const toggle = (k: string) => setSelected((sel) => (sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]));

    const target = useMemo(() => all.find((s) => s.photoSessionId === selected[0]) ?? null, [all, selected]);
    const targetLabel = target
        ? `the ${fmtWhen(when(target))} session${target.destination ? ` (${target.destination})` : ''}`
        : 'the first selected session';

    const merge = async () => {
        if (selected.length < 2 || merging) return;
        const count = selected.length;
        setMerging(true);
        setMergeError(null);
        try {
            await mergeSessions(selected[0], selected.slice(1));
            toast.show(`Merged ${count} sessions into one.`);
            await loadSessions();
            loadStats();
        } catch {
            setMergeError('Couldn’t merge the sessions — nothing changed. Try again.');
        }
        setMerging(false);
    };

    const openSession = (k: string) => navigate(`/activations/${id}/insights/session/${encodeURIComponent(k)}`);

    const maxShare = Math.max(1, ...(shareStats ?? []).map((r: any) => num(r.total)));
    const maxMedia = Math.max(1, ...(mediaStats ?? []).map((r: any) => num(r.total)));

    const sub =
        sessions === null
            ? sessionsFailed
                ? 'Couldn’t load sessions'
                : 'Loading sessions…'
            : `${all.length.toLocaleString()} session${all.length === 1 ? '' : 's'} · ${sharedCount.toLocaleString()} shared`;

    return (
        <div className="ev-page">
            <PageHeadActions
                title="Insights"
                sub={sub}
                actions={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setReportOpen(true)}>
                            Client report link
                        </button>
                        <button type="button" className="tb-new" onClick={() => navigate(`/activations/${id}/insights/session/new`)}>
                            New session
                        </button>
                    </>
                }
            />

            <Toolbar
                searchValue={search}
                onSearch={(v) => {
                    setSearch(v);
                    setLimit(PAGE);
                }}
                searchPlaceholder="Search recipient"
                filterCount={0}
            />

            <TCard>
                {sessionsFailed ? (
                    <TEmpty message="Couldn’t load sessions." actionLabel="Retry" onAction={loadSessions} />
                ) : sessions === null ? (
                    <TSkeleton rows={5} />
                ) : filtered.length === 0 ? (
                    <TEmpty
                        message={search.trim() ? 'No sessions match that recipient.' : 'No sessions yet — they appear as guests capture.'}
                        actionLabel={search.trim() ? 'Clear search' : undefined}
                        onAction={() => setSearch('')}
                    />
                ) : (
                    filtered.slice(0, limit).map((s) => {
                        const k: string | undefined = s.photoSessionId ?? undefined;
                        const on = !!k && selected.includes(k);
                        const t = k ? thumbs[k] ?? [] : [];
                        const photos = num(s.total);
                        const title = `${fmtWhen(when(s))} · ${photos} photo${photos === 1 ? '' : 's'}`;
                        const href = k ? `/activations/${id}/insights/session/${encodeURIComponent(k)}` : undefined;
                        return (
                            <div
                                key={s._key}
                                className={'trow ins-row' + (on ? ' on' : '') + (k ? '' : ' static')}
                                role={k ? 'link' : undefined}
                                tabIndex={k ? 0 : undefined}
                                onClick={() => k && openSession(k)}
                                onKeyDown={(e) => {
                                    if (k && e.key === 'Enter' && e.target === e.currentTarget) openSession(k);
                                }}
                            >
                                <span className="ins-sel" onClick={(e) => e.stopPropagation()}>
                                    {k && (
                                        <label className="check">
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                disabled={merging}
                                                aria-label={`Select the ${title} session`}
                                                onChange={() => toggle(k)}
                                            />
                                            <span className="box" aria-hidden="true">
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <path d="m5 12 4 4L19 6" />
                                                </svg>
                                            </span>
                                        </label>
                                    )}
                                </span>
                                <span className="ins-stack" aria-hidden="true">
                                    {t.length === 0 ? (
                                        <span className="thumb" />
                                    ) : (
                                        t.map((uri, ti) => <img key={ti} className="thumb" src={uri} alt="" loading="lazy" />)
                                    )}
                                </span>
                                <span className="main">
                                    <span className="nm">
                                        {href ? (
                                            <a
                                                href={href}
                                                tabIndex={-1}
                                                onClick={(e) => {
                                                    /* real link for ⌘-click; plain click uses the row */
                                                    if (!e.metaKey && !e.ctrlKey) e.preventDefault();
                                                    else e.stopPropagation();
                                                }}
                                            >
                                                {title}
                                            </a>
                                        ) : (
                                            title
                                        )}
                                    </span>
                                    <span className="sub">{s.destination || 'No share'}</span>
                                </span>
                                <span className={s.destination ? (s.sent ? 'ev-st ok' : 'ev-st') : 'ev-st muted'}>
                                    {s.destination ? (s.sent ? 'Sent' : 'Unsent') : '—'}
                                </span>
                            </div>
                        );
                    })
                )}
            </TCard>

            {sessions !== null && filtered.length > 0 && (
                <TFoot>
                    <div className="ev-foot">
                        <span>
                            {Math.min(limit, filtered.length).toLocaleString()} of {filtered.length.toLocaleString()}
                            {search.trim() ? ' · filtered' : ''}
                        </span>
                        {filtered.length > limit && (
                            <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                Show more
                            </button>
                        )}
                    </div>
                </TFoot>
            )}

            {selected.length > 0 && (
                <div className="savebar" role="region" aria-label="Selected sessions">
                    <span className="msg">
                        {selected.length} selected
                        {selected.length < 2 ? ' — select another to merge' : ''}
                        {mergeError && <span className="field-e"> · {mergeError}</span>}
                    </span>
                    <span className="sp" />
                    <button type="button" className="tb-btn ghost" disabled={merging} onClick={() => setSelected([])}>
                        Clear
                    </button>
                    <button
                        type="button"
                        className="tb-new"
                        disabled={selected.length < 2 || merging}
                        onClick={() => setConfirmMerge(true)}
                    >
                        {merging ? 'Merging…' : selected.length >= 2 ? `Merge ${selected.length} sessions` : 'Merge sessions'}
                    </button>
                </div>
            )}

            <div className="stack" style={{ marginTop: 24 }}>
                <ShareJourney id={id} />
            </div>

            <div className="ins-two" style={{ marginTop: 16 }}>
                <SectionCard title="Shares by day">
                    {shareStatsFailed ? (
                        <TEmpty message="Couldn’t load share stats." actionLabel="Retry" onAction={loadStats} />
                    ) : shareStats === null ? (
                        <TSkeleton rows={3} />
                    ) : shareStats.length === 0 ? (
                        <p className="quiet-empty">No shares yet.</p>
                    ) : (
                        <table className="ins-table">
                            <thead>
                                <tr>
                                    <th scope="col">Day</th>
                                    <th scope="col">Total</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">SMS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shareStats.map((r: any, i: number) => (
                                    <tr key={i}>
                                        <td className="strong">{(r.date ? moment(r.date) : moment(r.shareDate, 'MM/DD/YYYY')).format('MMM D')}</td>
                                        <td className="strong">
                                            <Bar value={num(r.total)} max={maxShare} />
                                            {num(r.total).toLocaleString()}
                                        </td>
                                        <td>{num(r.emailCount ?? r.email)}</td>
                                        <td>{num(r.textCount ?? r.text)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </SectionCard>
                <SectionCard title="Media by day">
                    {mediaStatsFailed ? (
                        <TEmpty message="Couldn’t load media stats." actionLabel="Retry" onAction={loadStats} />
                    ) : mediaStats === null ? (
                        <TSkeleton rows={3} />
                    ) : mediaStats.length === 0 ? (
                        <p className="quiet-empty">No media yet.</p>
                    ) : (
                        <table className="ins-table">
                            <thead>
                                <tr>
                                    <th scope="col">Day</th>
                                    <th scope="col">Total</th>
                                    <th scope="col">Photos</th>
                                    <th scope="col">Video</th>
                                    <th scope="col" className="wide">
                                        Prints
                                    </th>
                                    <th scope="col" className="wide">
                                        Other
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {mediaStats.map((r: any, i: number) => (
                                    <tr key={i}>
                                        <td className="strong">{moment(r.uploadDate, 'MM/DD/YYYY').format('MMM D')}</td>
                                        <td className="strong">
                                            <Bar value={num(r.total)} max={maxMedia} />
                                            {num(r.total).toLocaleString()}
                                        </td>
                                        <td>{num(r.photo)}</td>
                                        <td>{num(r.video)}</td>
                                        <td className="wide">{num(r.prints)}</td>
                                        <td className="wide">{num(r.original) + num(r.edited) + num(r.other)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </SectionCard>
            </div>

            <ConfirmDialog
                open={confirmMerge}
                title={`Merge ${selected.length} sessions?`}
                body={`The photos and shares from ${selected.length - 1} session${
                    selected.length - 1 === 1 ? '' : 's'
                } move into ${targetLabel}, the first one you selected. Merged sessions can’t be split apart again.`}
                actionLabel={`Merge ${selected.length} sessions`}
                danger
                onConfirm={merge}
                onClose={() => setConfirmMerge(false)}
            />
            <ReportLinkDialog id={id} open={reportOpen} onClose={() => setReportOpen(false)} />
            {toast.node}
        </div>
    );
}

export default function InsightsV3() {
    return (
        <EventPage section="insights">
            <InsightsBody />
        </EventPage>
    );
}
